/**
 * forms.js — localized validation.
 *
 * The browser's own validation bubbles are rendered in the *browser's* UI
 * language, not the site's, so a Persian visitor on an English-locale Windows
 * install saw "Please fill out this field." on a right-to-left form. Every
 * control here carries `novalidate` at the form level and is checked by this
 * module instead, with messages pulled from lib/i18n.
 *
 * Field direction is also set here rather than in CSS alone, because `dir` has
 * to be a real attribute for the caret and text selection to behave.
 */

import {qs, qsa, esc} from '../lib/dom.js';
import {messages, fieldDirection, toLatinDigits, num, gettext} from '../lib/i18n.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+]?[\d\s()-]{7,20}$/;
const URL_RE = /^https?:\/\/[^\s]+$/;

/* ------------------------------------------------------------------ *
 * Field-level
 * ------------------------------------------------------------------ */

function fieldWrapper(control) {
    return control.closest('.nv-field') || control.parentElement;
}

function showError(control, message) {
    const wrapper = fieldWrapper(control);
    if (!wrapper) return;

    wrapper.classList.add('is-invalid');
    wrapper.classList.remove('is-valid');

    let errorEl = qs('.nv-field__error', wrapper);
    if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'nv-field__error';
        errorEl.setAttribute('role', 'alert');
        wrapper.appendChild(errorEl);
    }

    errorEl.innerHTML =
        `<i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i><span>${esc(message)}</span>`;

    control.setAttribute('aria-invalid', 'true');

    // Link the message to the control so screen readers announce it.
    const id = errorEl.id || `err-${control.name || Math.random().toString(36).slice(2)}`;
    errorEl.id = id;
    control.setAttribute('aria-describedby', id);
}

function clearError(control, {markValid = false} = {}) {
    const wrapper = fieldWrapper(control);
    if (!wrapper) return;

    wrapper.classList.remove('is-invalid');
    wrapper.classList.toggle('is-valid', markValid);
    qs('.nv-field__error', wrapper)?.remove();
    control.removeAttribute('aria-invalid');
    control.removeAttribute('aria-describedby');
}

/**
 * Validate one control. Returns an error string, or null when valid.
 * Reads constraints from data-* attributes so the markup stays declarative.
 */
export function validateField(control) {
    const value = String(control.value || '').trim();
    const type = control.type || 'text';
    const required = control.hasAttribute('required') || control.dataset.required === 'true';

    if (required && !value) return messages.required();
    if (!value) return null; // optional and empty — nothing further to check

    const minLength = Number(control.dataset.minLength || control.minLength || 0);
    if (minLength > 0 && value.length < minLength) return messages.minLength(minLength);

    const maxLength = Number(control.dataset.maxLength || 0);
    if (maxLength > 0 && value.length > maxLength) return messages.maxLength(maxLength);

    if (type === 'email' && !EMAIL_RE.test(value)) return messages.email();
    if (type === 'url' && !URL_RE.test(value)) return messages.url();

    if (type === 'tel' || control.dataset.validate === 'phone') {
        // Normalise first: a number typed on a Persian keyboard is still a number.
        if (!PHONE_RE.test(toLatinDigits(value))) return messages.phone();
    }

    if (control.dataset.validate === 'numeric') {
        if (!/^\d+$/.test(toLatinDigits(value))) return messages.numeric();
    }

    return null;
}

/* ------------------------------------------------------------------ *
 * Form-level
 * ------------------------------------------------------------------ */

export function validateForm(form) {
    const controls = qsa('input, textarea, select', form)
        .filter((c) => c.type !== 'hidden' && !c.disabled);

    let firstInvalid = null;

    controls.forEach((control) => {
        const error = validateField(control);
        if (error) {
            showError(control, error);
            if (!firstInvalid) firstInvalid = control;
        } else {
            clearError(control, {markValid: Boolean(control.value)});
        }
    });

    if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.scrollIntoView({block: 'center', behavior: 'smooth'});
        return false;
    }

    return true;
}

/** Collect values, normalising digits so the API never receives ۱۲۳. */
export function serializeForm(form) {
    const data = {};
    qsa('input, textarea, select', form).forEach((control) => {
        if (!control.name || control.disabled) return;
        if (control.type === 'checkbox') {
            data[control.name] = control.checked;
        } else if (control.type === 'radio') {
            if (control.checked) data[control.name] = control.value;
        } else {
            data[control.name] = toLatinDigits(String(control.value || '').trim());
        }
    });
    return data;
}

/**
 * Attach validation to a form.
 * Fields validate on blur and re-validate live once they have been marked
 * invalid, which is the pattern that annoys people least.
 */
export function enhanceForm(form, {onSubmit} = {}) {
    if (!form || form.dataset.enhanced === 'true') return;
    form.dataset.enhanced = 'true';
    form.setAttribute('novalidate', 'novalidate');

    qsa('input, textarea, select', form).forEach((control) => {
        // Direction is decided per field, not per page.
        if (!control.getAttribute('dir')) {
            control.setAttribute('dir', fieldDirection(control.type, control.name || ''));
        }

        control.addEventListener('blur', () => {
            const error = validateField(control);
            if (error) showError(control, error);
            else clearError(control, {markValid: Boolean(control.value)});
        });

        control.addEventListener('input', () => {
            const wrapper = fieldWrapper(control);
            if (!wrapper?.classList.contains('is-invalid')) return;
            const error = validateField(control);
            if (!error) clearError(control, {markValid: true});
            else showError(control, error);
        });
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!validateForm(form)) return;
        if (typeof onSubmit === 'function') {
            await onSubmit(serializeForm(form), form);
        }
    });
}

/* ------------------------------------------------------------------ *
 * Feedback
 * ------------------------------------------------------------------ */

export function setFormBusy(form, busy) {
    const submit = qs('[type="submit"]', form);
    if (!submit) return;

    if (busy) {
        submit.dataset.label = submit.innerHTML;
        submit.disabled = true;
        submit.innerHTML =
            `<i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i><span>${gettext('Sending…')}</span>`;
    } else {
        submit.disabled = false;
        if (submit.dataset.label) submit.innerHTML = submit.dataset.label;
    }
}

export function showFormMessage(form, {type = 'info', text = ''}) {
    let box = qs('.nv-form__message', form);
    if (!box) {
        box = document.createElement('div');
        box.className = 'nv-form__message';
        form.prepend(box);
    }

    const icons = {
        success: 'circle-check',
        danger: 'circle-exclamation',
        warning: 'triangle-exclamation',
        info: 'circle-info',
    };

    box.innerHTML = `
    <div class="nv-alert nv-alert--${esc(type)}" role="status">
      <i class="fa-solid fa-${icons[type] || icons.info} nv-alert__icon" aria-hidden="true"></i>
      <div class="nv-alert__body">${esc(text)}</div>
    </div>`;

    box.scrollIntoView({block: 'nearest', behavior: 'smooth'});
}

/** Render a labelled field. Used by the quote/ticket form builder. */
export function fieldMarkup({
                                name, label, type = 'text', required = false,
                                placeholder = '', hint = '', rows = 0, options = null, value = '',
                            }) {
    const id = `f-${name}`;
    const dir = fieldDirection(type, name);
    const req = required ? ' required' : '';
    const reqMark = required ? '<span class="nv-field__required" aria-hidden="true">*</span>' : '';

    let control;
    if (rows > 0) {
        control = `<textarea id="${id}" name="${esc(name)}" class="nv-textarea" rows="${num(rows)}"
                 dir="${dir}" placeholder="${esc(placeholder)}"${req}>${esc(value)}</textarea>`;
    } else if (options) {
        control = `<select id="${id}" name="${esc(name)}" class="nv-select" dir="${dir}"${req}>
        ${options.map((o) => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join('')}
      </select>`;
    } else {
        control = `<input id="${id}" name="${esc(name)}" type="${esc(type)}" class="nv-input"
                 dir="${dir}" placeholder="${esc(placeholder)}" value="${esc(value)}"${req}>`;
    }

    return `
    <div class="nv-field">
      <label class="nv-field__label" for="${id}">${esc(label)}${reqMark}</label>
      ${control}
      ${hint ? `<span class="nv-field__hint">${esc(hint)}</span>` : ''}
    </div>`;
}
